import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { TypedMemo } from '../../common/types';
import { useUserToken } from '../../../redux/slices/appInfo/hooks';
import { refreshSessionToken, secondsUntilRefresh } from '../session/refreshSessionToken';
import { loginAsAdminOfToken, secondsUntilExpiry } from '../loginAs/loginAsClaim';
import { endExpiredLoginAsSession } from '../loginAs/loginAsSession';

const RETRY_AFTER_FAILURE_MS = 60 * 1000;

/**
 * Keeps the session token fresh. A token lives one hour; this refreshes it a
 * few minutes before it runs out, when the tab becomes visible again after a
 * sleep, and one minute after a failed attempt. Renders nothing.
 *
 * An admin session ("Log in as this user") is never refreshed: the API refuses it. This only
 * waits for the end of its token and then ends the session with a message.
 */
const SessionTokenRefresherComponent: React.FC = () => {
  const dispatch = useDispatch();
  const userToken = useUserToken();

  useEffect(() => {
    if (userToken === null || userToken === undefined || userToken === '') {
      return undefined;
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;
    const isAdminSession = loginAsAdminOfToken(userToken) !== null;

    const refreshIfDue = () => {
      if (cancelled) {
        return;
      }
      if (isAdminSession) {
        const secondsLeft = secondsUntilExpiry(userToken);
        if (secondsLeft === null) {
          return;
        }
        if (secondsLeft > 0) {
          clearTimeout(timer);
          timer = setTimeout(refreshIfDue, secondsLeft * 1000);
          return;
        }
        endExpiredLoginAsSession(dispatch);
        return;
      }
      const seconds = secondsUntilRefresh(userToken);
      if (seconds === null || cancelled) {
        return;
      }
      if (seconds > 0) {
        clearTimeout(timer);
        timer = setTimeout(refreshIfDue, seconds * 1000);
        return;
      }
      refreshSessionToken(userToken, dispatch)
        .then((outcome) => {
          if (outcome.kind === 'failed' && !cancelled) {
            clearTimeout(timer);
            timer = setTimeout(refreshIfDue, RETRY_AFTER_FAILURE_MS);
          }
        })
        .catch(console.error);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshIfDue();
      }
    };

    refreshIfDue();
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [dispatch, userToken]);

  return null;
};

export const SessionTokenRefresher = TypedMemo(SessionTokenRefresherComponent);
